import { useEffect, useCallback, RefObject } from 'react';
import { toast } from 'react-toastify';

import { DEFAULT_AREA_ID, INSL_ZONE_LABEL_UNAME as UNZONE } from '@/config';

import { Commander } from '../bridge/commander';
import { NullableBPMap } from '../map/BPMapAPI';
import { FITTINGDTL } from '../map/config';
import { ZoneAttrDetail } from '../panels/panel-props';
import { panelOpenStore } from '../store/usePanelOpenStore';
import { hasInsulationDefined } from '../store';
import {
  GO,
  PANEL,
  InsulationAreaSetting,
  TKEvent as EVT,
  InsuEnum,
  DEFAULT_INSULATION,
  generateInsulationSetting,
} from '../types';
import { PAGE_UNIT } from '../types/options';

// Reverse map: backend field name → frontend InsulationAreaSetting key
const BACKEND_TO_FRONTEND: Partial<
  Record<string, keyof InsulationAreaSetting>
> = {
  [InsuEnum.AN]: 'areaName',
  [InsuEnum.AT]: 'areaMode',
  [InsuEnum.SC]: 'serveCategory',
  [InsuEnum.IX]: 'exposeType',
  [InsuEnum.IT]: 'insulationType',
  [InsuEnum.IC]: 'insulationThickness',
  [InsuEnum.CT]: 'claddingType',
};

// Module-level cache — persists default area insulation settings across panel open/close
let defaultAreaSettingsCache: InsulationAreaSetting = { ...DEFAULT_INSULATION };

/**
 * Cache insulation property update for Default Area in the zone-properties
 */
export const usePolygonPropsPanel = (mapRef: RefObject<NullableBPMap>) => {
  // Listen to POLYGON_PROP_CHANGED events for DEFAULT_AREA_ID and update cache
  useEffect(() => {
    const handler = (e: Event) => {
      const { detail } = e as CustomEvent;
      const { plgId, ...backendChanges } = detail;
      if (plgId !== DEFAULT_AREA_ID) return;
      const frontendChanges: Partial<InsulationAreaSetting> = {};
      for (const [backendField, value] of Object.entries(backendChanges)) {
        const frontendField = BACKEND_TO_FRONTEND[backendField];
        if (frontendField && frontendField !== 'areaMode') {
          (frontendChanges as Record<string, unknown>)[frontendField] = value;
        }
      }

      defaultAreaSettingsCache = {
        ...defaultAreaSettingsCache,
        ...frontendChanges,
      };
    };
    document.addEventListener(EVT.POLYGON_PROP_CHANGED, handler);
    return () =>
      document.removeEventListener(EVT.POLYGON_PROP_CHANGED, handler);
  }, []);

  /**
   * Open the PolygonZoneProperties panel for the selected insulation area
   */
  const openAreaPanel = useCallback(
    (areaId: string | null) => {
      if (!areaId || !mapRef.current) return;

      const map = mapRef.current;
      const page = map.getPageObj();
      const pageContext = {
        pageLocked: page?.locked,
        unit: page?.unit || PAGE_UNIT.I,
      };
      // ! strictly filter out those need insulation
      // @2026/06/30
      // ! we shouldnt check whether or not the symbol have insulated,
      // ! because user may modify the default area insulation properties!
      // @2026/07/14
      const uninsulated = (s: GO) => {
        return (
          !!s?.ukey && hasInsulationDefined(s.entityType)
          // && !map.searchInsulationBy(s.ukey)
        );
      };

      let zoneDetail: ZoneAttrDetail;

      if (areaId === DEFAULT_AREA_ID) {
        const symbolsInZone: GO[] = map
          .getFeaturesNotCoveredByPolygons()
          .map((f) => f.get(FITTINGDTL));
        const symbolsToInsulate = symbolsInZone.filter(uninsulated);

        // Lazily create the default-area polygon the first time the zone panel
        // opens for it, so any subsequent insulation creation always has a real
        // polygon symbol to reference.
        let defaultArea = map.getSymbolBy(DEFAULT_AREA_ID);
        if (!defaultArea) {
          map.buildDefaultAreaWith(DEFAULT_INSULATION);
          defaultArea = map.getSymbolBy(DEFAULT_AREA_ID);
          // popup tips...@2026/08/19
          toast.info('Default area created!');
        }
        const savedInsulation = defaultArea
          ? generateInsulationSetting(defaultArea.detail, true)
          : {};
        zoneDetail = {
          id: DEFAULT_AREA_ID,
          ...defaultAreaSettingsCache,
          areaName: UNZONE,
          areaMode: 'default',
          ...savedInsulation,
          symbolsInZone,
          symbolsToInsulate,
          ...pageContext,
        };
      } else {
        const feature = map.getFeatureById(areaId);
        if (!feature) return;
        const symbolsInZone: GO[] =
          map.getFeaturesIntersectWith(areaId)?.map((f) => f.get(FITTINGDTL)) ||
          [];
        const symbolsToInsulate = symbolsInZone.filter(uninsulated);
        const detail = feature.get(FITTINGDTL);
        zoneDetail = {
          ...detail,
          areaMode: 'polygon',
          areaName: detail['area_name'],
          exposeType: detail['i_expose_type'],
          serveCategory: detail['i_serve_category'],
          symbolsInZone,
          symbolsToInsulate,
          ...pageContext,
        };
      }

      Commander.openZonePropertiesPanel(zoneDetail);
      panelOpenStore.setPanelOpen(PANEL.Z);
    },
    [mapRef],
  );

  return {
    /**
     * Open the PolygonZoneProperties panel for the selected insulation area
     */
    openAreaPanel,
  };
};
